/**
 * Headcount Normalization
 * 
 * Normalizes various headcount data formats to the canonical model.
 * Handles legacy formats and ensures correct total calculation.
 */

import { 
  createEmptyHeadcount, 
  getHeadcountTotal,
  getHeadcountDisplayInfo,
  isTotalLabel,
  isSubcategoryLabel
} from './models/headcount';

/**
 * Normalize headcount from various sources to canonical model
 * @param {Object|Array} raw - Raw headcount data (various formats)
 * @returns {Object} - Canonical headcount model
 */
export function normalizeHeadcount(raw) {
  if (!raw) {
    return createEmptyHeadcount();
  }

  // Already in canonical format (total is an object with value property)
  if (raw.total !== undefined && typeof raw.total === 'object' && raw.total !== null && !Array.isArray(raw)) {
    return {
      total: raw.total,
      byFunction: raw.byFunction || [],
      otherEntities: raw.otherEntities || [],
      ambiguity: raw.ambiguity
    };
  }

  // Legacy: headcountByFunction array (old deal profile format)
  if (Array.isArray(raw)) {
    return normalizeLegacyByFunction(raw);
  }

  // Legacy: object with total (number) and by_function/byFunction
  if (raw.total !== undefined || raw.by_function !== undefined || raw.byFunction !== undefined) {
    return normalizeLegacyObject(raw);
  }

  // Legacy: flat number
  if (typeof raw === 'number') {
    return {
      total: {
        value: raw,
        period: null,
        source: 'Document',
        evidence: []
      },
      byFunction: [],
      otherEntities: [],
      ambiguity: undefined
    };
  }

  return createEmptyHeadcount();
}

/**
 * Normalize legacy headcountByFunction array
 * @param {Array} byFunction - Array of {function, count, evidence}
 * @returns {Object} - Canonical headcount model
 */
function normalizeLegacyByFunction(byFunction) {
  if (!Array.isArray(byFunction) || byFunction.length === 0) {
    return createEmptyHeadcount();
  }

  // Check if any entry looks like a total
  const totals = byFunction.filter(h => isTotalLabel(h.function));
  const subcategories = byFunction.filter(h => !isTotalLabel(h.function));

  // If we found explicit totals, use the first one
  let selectedTotal = null;
  let ambiguity = undefined;

  if (totals.length > 0) {
    selectedTotal = {
      value: totals[0].count,
      period: null,
      source: 'Document',
      evidence: totals[0].evidence ? [{ snippet: totals[0].evidence }] : []
    };

    if (totals.length > 1) {
      ambiguity = {
        multipleTotalsFound: true,
        candidates: totals.map(t => ({
          value: t.count,
          period: null,
          evidence: t.evidence ? [{ snippet: t.evidence }] : []
        })),
        resolutionRule: 'Selected first total found'
      };
    }
  }

  return {
    total: selectedTotal,
    byFunction: subcategories.map(h => ({
      function: h.function || 'Unknown',
      count: h.count || 0,
      source: 'Document',
      evidence: h.evidence ? [{ snippet: h.evidence }] : [],
      isSubsetOfTotal: true
    })),
    otherEntities: [],
    ambiguity
  };
}

/**
 * Normalize legacy object format
 * @param {Object} obj - Legacy headcount object
 * @returns {Object} - Canonical headcount model
 */
function normalizeLegacyObject(obj) {
  if (!obj || typeof obj !== 'object') {
    return createEmptyHeadcount();
  }

  // Handle total as number or object
  let total = null;
  if (obj.total !== undefined && obj.total !== null) {
    if (typeof obj.total === 'number') {
      total = {
        value: obj.total,
        period: null,
        source: 'Document',
        evidence: []
      };
    } else if (typeof obj.total === 'object') {
      total = {
        value: obj.total.value,
        period: obj.total.period || null,
        source: obj.total.source || 'Document',
        evidence: obj.total.evidence || []
      };
    }
  }

  // Handle byFunction/by_function
  const byFunctionRaw = obj.byFunction || obj.by_function || [];
  const byFunction = byFunctionRaw.map(h => ({
    function: h.function || 'Unknown',
    count: h.count || 0,
    source: h.source || 'Document',
    evidence: h.evidence || [],
    isSubsetOfTotal: h.isSubsetOfTotal !== false
  }));

  return {
    total,
    byFunction,
    otherEntities: obj.otherEntities || [],
    ambiguity: obj.ambiguity
  };
}

/**
 * Normalize deal profile to use canonical headcount model
 * @param {Object} profile - Raw deal profile
 * @returns {Object} - Profile with normalized headcount
 */
export function normalizeDealProfileHeadcount(profile) {
  if (!profile) return profile;

  const normalized = { ...profile };

  // Handle new headcount structure
  if (profile.headcount) {
    normalized.headcount = normalizeHeadcount(profile.headcount);
  } 
  // Handle legacy headcountByFunction
  else if (profile.headcountByFunction) {
    normalized.headcount = normalizeLegacyByFunction(profile.headcountByFunction);
  } else {
    // Initialize empty headcount if none exists
    normalized.headcount = createEmptyHeadcount();
  }

  // Keep legacy field for backward compatibility but don't use for display
  if (profile.headcountByFunction && !normalized.headcountByFunction) {
    normalized.headcountByFunction = profile.headcountByFunction;
  }

  return normalized;
}

/**
 * Get headcount total for display (legacy compatibility)
 * @param {Object} profile - Deal profile
 * @returns {number|null}
 */
export function getDisplayHeadcount(profile) {
  if (!profile) return null;

  // Use canonical headcount if available
  if (profile.headcount) {
    const info = getHeadcountDisplayInfo(profile.headcount);
    return info.value;
  }

  // Legacy fallback
  if (profile.headcountByFunction) {
    // Check if there's an explicit total in the array
    const totalEntry = profile.headcountByFunction.find(h => 
      isTotalLabel(h.function)
    );
    if (totalEntry) {
      return totalEntry.count;
    }

    // DON'T sum by default - only if explicitly marked
    const allMarkedComplete = profile.headcountByFunction.every(h =>
      h.isCompleteWorkforce || h.representsFullWorkforce
    );
    
    if (allMarkedComplete) {
      return profile.headcountByFunction.reduce((sum, h) => sum + (h.count || 0), 0);
    }
  }

  return null;
}

/**
 * Format headcount for display with warning
 * @param {Object} profile - Deal profile
 * @returns {{value: string, warning: string|null, hasAmbiguity: boolean}}
 */
export function formatHeadcountDisplay(profile) {
  const displayInfo = profile?.headcount 
    ? getHeadcountDisplayInfo(profile.headcount)
    : { value: getDisplayHeadcount(profile), warning: null, isSubsetSum: false };

  const hasAmbiguity = profile?.headcount?.ambiguity?.multipleTotalsFound || false;

  return {
    value: displayInfo.value !== null ? displayInfo.value.toLocaleString() : 'N/A',
    warning: displayInfo.warning,
    hasAmbiguity,
    candidates: profile?.headcount?.ambiguity?.candidates || null
  };
}

export default {
  normalizeHeadcount,
  normalizeDealProfileHeadcount,
  getDisplayHeadcount,
  formatHeadcountDisplay
};

// Re-export from headcount model for convenience
export { getHeadcountDisplayInfo } from './models/headcount';
